/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import 'twin.macro';
import tw from 'twin.macro';

import { ReactComponent as VerifiedSvg } from 'assets/Icons/verified.svg';
import { QuestionHint } from 'components/shared/QuestionHint';
import { customTooltipCss } from 'components/Preferences/Integrations/IntegrationItem';
import { DebouncedFunc, debounce, findIndex, isArray, isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Option } from 'components/shared/CustomSelectSearch';
import { SimplecastService } from 'services/simplecast.service';
import {
  addSimplecastIntegrationFilter,
  removeSimplecastIntegrationFilter,
  updateSimplecastProgramFilter,
  updateSingleSimplecastIntegration,
} from 'slices/simplecast.slice';
import { ProgramIdOption, SimplecastIntegrationOption } from 'utils/models';
import { SIMPLECAST_PUSH_CAPABILITIES } from 'utils/simplecastPushCapabilities';
import { customToast } from 'utils/toast.util';
import { SimplecastIntegrationFilterItem } from './SimplecastIntegrationFilterItem';

const RSS_FEED_HINT =
  'Polled more frequently than the Simplecast API for faster episode detection.';

type Props = {
  integration: SimplecastIntegrationOption;
  programIds: ProgramIdOption[];
  isLoading?: boolean;
};

type ValidateRssArgs = {
  index: number;
  integrationId: string;
  rssFeedUrl: string;
  apiKey: string;
  programId: string;
};

export const SimplecastIntegrationFilter = ({
  integration,
  programIds,
  isLoading = false,
}: Props) => {
  const dispatch = useDispatch();

  const [listProgramIdsDropdown, setListProgramIdsDropdown] =
    useState<ProgramIdOption[]>();
  const validationRequestIds = useRef<Record<number, number>>({});
  const debouncedValidateByIndex = useRef<
    Record<number, DebouncedFunc<(args: ValidateRssArgs) => void>>
  >({});

  const lastProgramFilterIndex = integration.filter.programFilters.length - 1;

  useEffect(() => {
    if (isEmpty(programIds) && isArray(programIds)) {
      resetProgramList();
      return;
    }

    setListProgramIdsDropdown(programIds);
  }, [programIds]);

  useEffect(() => {
    return () => {
      Object.values(debouncedValidateByIndex.current).forEach((fn) =>
        fn.cancel(),
      );
    };
  }, []);

  const handleSelectProgramId = (newValue: Option, index: number) => {
    if (isEmpty(newValue.value) && isEmpty(newValue.label)) return;

    const newIntegration = { ...integration };
    const newProgramFilter = [...newIntegration.filter.programFilters];

    const foundDuplicate = findIndex(
      newProgramFilter,
      (filter) => filter.programId === newValue.value,
    );

    if (foundDuplicate >= 0 && foundDuplicate !== index) {
      customToast.error('Podcast ID already exists');
      return;
    }

    const foundProgramFilter = { ...newProgramFilter[index] };

    foundProgramFilter.programId = newValue.value;
    foundProgramFilter.programName = newValue.label;
    foundProgramFilter.rssValidationStatus = foundProgramFilter.rssFeedUrl
      ? 'unvalidated'
      : undefined;
    foundProgramFilter.rssValidationMessage = undefined;

    newProgramFilter[index] = foundProgramFilter;

    dispatch(
      updateSingleSimplecastIntegration({
        ...newIntegration,
        filter: {
          ...integration.filter,
          programFilters: newProgramFilter,
        },
      }),
    );
  };

  const resetProgramList = () => {
    setListProgramIdsDropdown([]);
    handleSelectProgramId(
      {
        value: '',
        label: '',
      },
      0,
    );
  };

  const handleAddNewFilter = () => {
    dispatch(addSimplecastIntegrationFilter(integration));
  };

  const handleRemoveFilter = (index: number) => {
    dispatch(
      removeSimplecastIntegrationFilter({
        integration,
        index,
      }),
    );
  };

  const validateRssFeed = async ({
    index,
    integrationId,
    rssFeedUrl,
    apiKey,
    programId,
  }: ValidateRssArgs) => {
    if (isEmpty(rssFeedUrl)) {
      dispatch(
        updateSimplecastProgramFilter({
          integrationId,
          index,
          changes: {
            rssValidationStatus: undefined,
            rssValidationMessage: undefined,
          },
        }),
      );
      return;
    }

    if (!apiKey || !programId) return;

    const requestId = (validationRequestIds.current[index] ?? 0) + 1;
    validationRequestIds.current[index] = requestId;
    dispatch(
      updateSimplecastProgramFilter({
        integrationId,
        index,
        changes: {
          rssValidationStatus: 'validating',
          rssValidationMessage: undefined,
        },
      }),
    );

    try {
      const response = await SimplecastService.validateRssFeed({
        apiKey,
        programId,
        rssFeedUrl,
      });
      if (validationRequestIds.current[index] !== requestId) return;

      dispatch(
        updateSimplecastProgramFilter({
          integrationId,
          index,
          changes: {
            rssValidationStatus: response.data.valid ? 'valid' : 'invalid',
            rssValidationMessage: response.data.message ?? undefined,
          },
        }),
      );
    } catch (err: any) {
      if (validationRequestIds.current[index] !== requestId) return;
      const isInvalidApiKey = err?.response?.status === 401;

      dispatch(
        updateSimplecastProgramFilter({
          integrationId,
          index,
          changes: {
            rssValidationStatus: isInvalidApiKey ? 'invalid' : 'error',
            rssValidationMessage: err?.response?.data?.message ?? undefined,
          },
        }),
      );
    }
  };

  const scheduleValidateRssFeed = (args: ValidateRssArgs) => {
    const { index } = args;
    if (!debouncedValidateByIndex.current[index]) {
      debouncedValidateByIndex.current[index] = debounce(
        (validateArgs: ValidateRssArgs) => {
          void validateRssFeed(validateArgs);
        },
        300,
      );
    }

    debouncedValidateByIndex.current[index](args);
  };

  const handleRssFeedUrlChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    validationRequestIds.current[index] =
      (validationRequestIds.current[index] ?? 0) + 1;
    const rssFeedUrl = e.target.value;
    const trimmedRssFeedUrl = rssFeedUrl.trim();

    dispatch(
      updateSimplecastProgramFilter({
        integrationId: integration.id,
        index,
        changes: {
          rssFeedUrl,
          rssValidationStatus: isEmpty(trimmedRssFeedUrl)
            ? undefined
            : 'unvalidated',
          rssValidationMessage: undefined,
        },
      }),
    );

    if (isEmpty(trimmedRssFeedUrl)) {
      debouncedValidateByIndex.current[index]?.cancel();
      return;
    }

    const apiKey = integration.apiKey?.trim() ?? '';
    const programId =
      integration.filter.programFilters[index]?.programId?.trim() ?? '';
    if (!apiKey || !programId) return;

    scheduleValidateRssFeed({
      index,
      integrationId: integration.id,
      rssFeedUrl: trimmedRssFeedUrl,
      apiKey,
      programId,
    });
  };

  const renderRssValidationMessage = (
    status: (typeof integration.filter.programFilters)[number]['rssValidationStatus'],
    message?: string,
  ) => {
    if (status === 'validating') {
      return (
        <small tw="text-10 text-sonnant-orange">
          Validating RSS Feed...
        </small>
      );
    }
    if (!message) {
      return null;
    }

    if (status === 'valid') {
      return (
        <small tw="flex gap-1.5 items-center text-10 text-sonnant-green font-medium">
          {message}
          <VerifiedSvg tw="zoom[0.9]" />
        </small>
      );
    }

    return <small tw="text-10 text-sonnant-red">{message}</small>;
  };

  const handleChangeIntegration = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    dispatch(
      updateSingleSimplecastIntegration({
        ...integration,
        filter: {
          ...integration.filter,
          [name]: checked,
        },
      }),
    );
  };

  const handleChangeIntegrationData = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, checked } = e.target;

    dispatch(
      updateSingleSimplecastIntegration({
        ...integration,
        filter: {
          ...integration.filter,
          integrationSettings: {
            ...integration.filter.integrationSettings,
            [name]: checked,
          },
        },
      }),
    );
  };

  const settings = integration.filter.integrationSettings;

  return (
    <div tw="sm-up:w-[75%] lg-up:w-full xl-up:w-[80%] 2xl-up:w-1/2 mt-[1.5rem] flex flex-col pl-[3rem]">
        <div tw="flex flex-col gap-y-[1.5rem] items-start">
          {integration.filter.programFilters?.map((filter, index) => {
            const isRssDisabled =
              isEmpty(filter.programId?.trim()) ||
              isEmpty(filter.programName?.trim());

            return (
              <div key={index} tw="flex flex-col gap-y-[1rem]">
                <SimplecastIntegrationFilterItem
                  filter={filter}
                  index={index}
                  lastProgramFilterIndex={lastProgramFilterIndex}
                  programIdOptions={listProgramIdsDropdown}
                  isLoading={isLoading}
                  onSelectProgramId={handleSelectProgramId}
                  onAddNewFilter={handleAddNewFilter}
                  onRemoveFilter={handleRemoveFilter}
                />

                <div tw="flex-col mt-3 w-[56rem]">
                  <div tw="flex flex-col pt-0!">
                    <div tw="flex flex-row items-center">
                      <label tw="flex flex-nowrap text-16 mr-3">
                        RSS Feed URL (optional)
                      </label>
                      <QuestionHint
                        notTransparent
                        hideCloseButton
                        type={'information'}
                        align={'bottomLeft'}
                        action={'click'}
                        text={<div>{RSS_FEED_HINT}</div>}
                        customStyle={customTooltipCss}
                      />
                    </div>

                    <div tw="flex flex-row w-full mb-[1rem]">
                      <div tw="flex-1 relative">
                        <input
                          tw="flex mb-0"
                          css={[
                            ['invalid', 'error'].includes(
                              filter.rssValidationStatus ?? '',
                            ) &&
                              tw`(border-red-500 border-width[1px])!`,
                          ]}
                          type="url"
                          disabled={isRssDisabled}
                          value={filter.rssFeedUrl ?? ''}
                          onChange={(e) => handleRssFeedUrlChange(e, index)}
                          placeholder="https://feeds.simplecast.com/xxxxxxxx"
                        />
                        {renderRssValidationMessage(
                          filter.rssValidationStatus,
                          filter.rssValidationMessage,
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      <div tw="mt-5">
        <label tw="max-w-full flex[unset]! cursor-pointer">
          <input
            name="isAutomatePushBack"
            checked={integration.filter.isAutomatePushBack}
            onChange={handleChangeIntegration}
            type="checkbox"
          />
          <span className="checkmark" style={{ top: '4px' }}></span>
          Automate integration back to Simplecast
        </label>
      </div>

      <div tw="my-5">
        <label tw="flex flex-nowrap text-16 mr-3">Integration data</label>
        <div tw="flex justify-between mt-3">
          <div tw="w-[25rem]">
            {SIMPLECAST_PUSH_CAPABILITIES.hasMonetisation && (
              <label tw="max-w-full flex w-1/2 flex[unset]! cursor-pointer">
                <input
                  name="hasMonetisation"
                  checked={settings?.hasMonetisation}
                  onChange={handleChangeIntegrationData}
                  type="checkbox"
                />
                <span className="checkmark" style={{ top: '4px' }}></span>
                Monetisation
              </label>
            )}

            {SIMPLECAST_PUSH_CAPABILITIES.hasTags && (
              <label tw="max-w-full flex w-1/2 flex[unset]! cursor-pointer">
                <input
                  name="hasTags"
                  checked={settings?.hasTags}
                  onChange={handleChangeIntegrationData}
                  type="checkbox"
                />
                <span className="checkmark" style={{ top: '4px' }}></span>
                Tags
              </label>
            )}
          </div>

          <div tw="flex-auto">
            {SIMPLECAST_PUSH_CAPABILITIES.hasSummary && (
              <label tw="max-w-full flex w-1/2 flex[unset]! cursor-pointer">
                <input
                  name="hasSummary"
                  checked={settings?.hasSummary}
                  onChange={handleChangeIntegrationData}
                  type="checkbox"
                />
                <span className="checkmark" style={{ top: '4px' }}></span>
                Description/Summary
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
